import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Textarea } from 'components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { 
  ArrowLeft, Flag, Shield, Users, Clock, CheckCircle, 
  XCircle, AlertTriangle, User, Eye, FileText, Search, MapPin, Navigation
} from 'lucide-react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icons for different marker types
const createIcon = (color) => new L.Icon({
  iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const verifiedIcon = createIcon('green');
const unverifiedIcon = createIcon('red');
const expectedIcon = createIcon('blue');

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [flaggedShifts, setFlaggedShifts] = useState([]);
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [backgroundChecks, setBackgroundChecks] = useState([]);
  const [users, setUsers] = useState([]);
  const [shiftsWithLocation, setShiftsWithLocation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [bgCheckDialogOpen, setBgCheckDialogOpen] = useState(false);
  const [selectedBgCheck, setSelectedBgCheck] = useState(null);
  const [bgCheckResult, setBgCheckResult] = useState('clear');
  const [bgCheckNotes, setBgCheckNotes] = useState('');
  const [selectedShiftForMap, setSelectedShiftForMap] = useState(null);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [shiftsRes, verificationsRes, usersRes, bgChecksRes, locationShiftsRes] = await Promise.all([
        api.get(`/admin/flagged-shifts`),
        api.get(`/admin/verification-requests`),
        api.get(`/admin/users`),
        api.get(`/admin/background-checks`),
        api.get(`/admin/shifts-with-location`).catch(() => ({ data: { shifts: [] } }))
      ]);
      setFlaggedShifts(shiftsRes.data.shifts || []);
      setVerificationRequests(verificationsRes.data.requests || []);
      setUsers(usersRes.data.users || []);
      setBackgroundChecks(bgChecksRes.data.checks || []);
      setShiftsWithLocation(locationShiftsRes.data.shifts || []);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResolveShift = async (shiftId, action) => {
    try {
      await api.post(`/admin/shifts/${shiftId}/resolve`, { action });
      toast.success(`Shift ${action}d successfully`);
      fetchData();
    } catch (error) {
      toast.error('Failed to resolve shift');
    }
  };

  const handleSuspendUser = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/suspend`, {});
      toast.success('User suspended');
      fetchData();
    } catch (error) {
      toast.error('Failed to suspend user');
    }
  };

  const handleViewDocument = async (requestId) => {
    try {
      const response = await api.get(`/admin/verification/${requestId}/document`);
      setDocumentPreview(response.data.document_data);
      const verification = verificationRequests.find(v => v.request_id === requestId);
      setSelectedVerification(verification);
      setReviewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load document');
    }
  };

  const handleReviewVerification = async (action) => {
    if (!selectedVerification) return;
    try {
      await api.post(`/admin/verification/${selectedVerification.request_id}/review`, {
        action,
        rejection_reason: action === 'reject' ? rejectionReason : null
      });
      toast.success(`Verification ${action}d successfully`);
      setReviewDialogOpen(false);
      setSelectedVerification(null);
      setDocumentPreview(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${action} verification`);
    }
  };

  const handleProcessBgCheck = async (checkId) => {
    try {
      await api.post(`/admin/background-check/${checkId}/process`, {});
      toast.success('Background check marked as in progress');
      fetchData();
    } catch (error) {
      toast.error('Failed to process background check');
    }
  };

  const handleCompleteBgCheck = async () => {
    if (!selectedBgCheck) return;
    try {
      await api.post(`/admin/background-check/${selectedBgCheck.check_id}/complete`, {
        result: bgCheckResult,
        notes: bgCheckNotes,
        result_details: []
      });
      toast.success(`Background check completed: ${bgCheckResult}`);
      setBgCheckDialogOpen(false);
      setSelectedBgCheck(null);
      setBgCheckResult('clear');
      setBgCheckNotes('');
      fetchData();
    } catch (error) {
      toast.error('Failed to complete background check');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse-soft">
          <div className="w-12 h-12 rounded-full bg-teal-500/20 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full bg-teal-500"></div>
          </div>
        </div>
      </div>
    );
  }

  const pendingVerifications = verificationRequests.filter(v => v.status === 'pending');
  const pendingBgChecks = backgroundChecks.filter(c => c.status === 'pending' || c.status === 'in_progress');

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Admin Dashboard</h1>
            <p className="text-xs sm:text-sm text-slate-500">Manage verifications, shifts, and users</p>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <Shield className="w-8 h-8 text-teal-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{pendingVerifications.length}</p>
              <p className="text-sm text-slate-500">ID Verifications</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Search className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{pendingBgChecks.length}</p>
              <p className="text-sm text-slate-500">Background Checks</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Flag className="w-8 h-8 text-rose-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{flaggedShifts.length}</p>
              <p className="text-sm text-slate-500">Flagged Shifts</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{users.length}</p>
              <p className="text-sm text-slate-500">Total Users</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">
                {users.filter(u => u.role === 'independent_caregiver').length}
              </p>
              <p className="text-sm text-slate-500">Caregivers</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="background">
          <TabsList className="grid grid-cols-3 sm:grid-cols-5 w-full mb-4 sm:mb-6">
            <TabsTrigger value="background" className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              BG Checks ({pendingBgChecks.length})
            </TabsTrigger>
            <TabsTrigger value="verifications" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              ID Verify ({pendingVerifications.length})
            </TabsTrigger>
            <TabsTrigger value="location" className="flex items-center gap-2" data-testid="location-tab">
              <MapPin className="w-4 h-4" />
              Location ({shiftsWithLocation.filter(s => s.location_verified === false).length})
            </TabsTrigger>
            <TabsTrigger value="flagged" className="flex items-center gap-2">
              <Flag className="w-4 h-4" />
              Shifts
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Users
            </TabsTrigger>
          </TabsList>

          {/* Background Checks */}
          <TabsContent value="background">
            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope']">Background Check Requests</CardTitle>
              </CardHeader>
              <CardContent>
                {backgroundChecks.length === 0 ? (
                  <div className="text-center py-8">
                    <Search className="w-12 h-12 text-blue-300 mx-auto mb-3" />
                    <p className="text-slate-500">No background check requests</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {backgroundChecks.map((check) => (
                      <div key={check.check_id} className="p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              check.status === 'pending' ? 'bg-amber-100' :
                              check.status === 'in_progress' ? 'bg-blue-100' :
                              check.status === 'clear' ? 'bg-teal-100' :
                              'bg-rose-100'
                            }`}>
                              {check.status === 'clear' ? (
                                <CheckCircle className="w-6 h-6 text-teal-600" />
                              ) : check.status === 'flagged' || check.status === 'failed' ? (
                                <XCircle className="w-6 h-6 text-rose-600" />
                              ) : check.status === 'in_progress' ? (
                                <Search className="w-6 h-6 text-blue-600" />
                              ) : (
                                <Clock className="w-6 h-6 text-amber-600" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={
                                  check.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                  check.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                  check.status === 'clear' ? 'bg-teal-100 text-teal-700' :
                                  'bg-rose-100 text-rose-700'
                                }>
                                  {check.status === 'in_progress' ? 'In Progress' : check.status?.charAt(0).toUpperCase() + check.status?.slice(1)}
                                </Badge>
                                <Badge variant="outline">{check.package_name}</Badge>
                              </div>
                              <p className="font-medium text-slate-900">{check.user_name}</p>
                              <p className="text-sm text-slate-500">{check.user_email}</p>
                              <p className="text-xs text-slate-400 mt-1">
                                Initiated: {new Date(check.initiated_at).toLocaleString()}
                              </p>
                              {check.personal_info && (
                                <p className="text-xs text-slate-500 mt-1">
                                  DOB: {check.personal_info.date_of_birth} | 
                                  {check.personal_info.address?.city}, {check.personal_info.address?.state}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {check.status === 'pending' && (
                              <Button
                                size="sm"
                                className="bg-blue-500 hover:bg-blue-600 text-white"
                                onClick={() => handleProcessBgCheck(check.check_id)}
                              >
                                <Search className="w-4 h-4" />
                                Start Processing
                              </Button>
                            )}
                            {check.status === 'in_progress' && (
                              <Button
                                size="sm"
                                className="bg-teal-500 hover:bg-teal-600 text-white"
                                onClick={() => {
                                  setSelectedBgCheck(check);
                                  setBgCheckDialogOpen(true);
                                }}
                              >
                                <CheckCircle className="w-4 h-4" />
                                Complete
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ID Verifications */}
          <TabsContent value="verifications">
            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope']">ID Verification Requests</CardTitle>
              </CardHeader>
              <CardContent>
                {verificationRequests.length === 0 ? (
                  <div className="text-center py-8">
                    <Shield className="w-12 h-12 text-teal-300 mx-auto mb-3" />
                    <p className="text-slate-500">No verification requests</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {verificationRequests.map((request) => (
                      <div key={request.request_id} className="p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              request.status === 'pending' ? 'bg-amber-100' :
                              request.status === 'approved' ? 'bg-teal-100' : 'bg-rose-100'
                            }`}>
                              {request.status === 'approved' ? (
                                <CheckCircle className="w-6 h-6 text-teal-600" />
                              ) : request.status === 'rejected' ? (
                                <XCircle className="w-6 h-6 text-rose-600" />
                              ) : (
                                <Clock className="w-6 h-6 text-amber-600" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={
                                  request.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                  request.status === 'approved' ? 'bg-teal-100 text-teal-700' :
                                  'bg-rose-100 text-rose-700'
                                }>
                                  {request.status?.charAt(0).toUpperCase() + request.status?.slice(1)}
                                </Badge>
                                <Badge variant="outline">{request.document_type?.replace('_', ' ')}</Badge>
                              </div>
                              <p className="font-medium text-slate-900">{request.user_name}</p>
                              <p className="text-sm text-slate-500">{request.user_email}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleViewDocument(request.request_id)}>
                              <Eye className="w-4 h-4" />
                              View
                            </Button>
                            {request.status === 'pending' && (
                              <Button
                                size="sm"
                                className="bg-teal-500 hover:bg-teal-600 text-white"
                                onClick={() => {
                                  setSelectedVerification(request);
                                  handleReviewVerification('approve');
                                }}
                              >
                                <CheckCircle className="w-4 h-4" />
                                Approve
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Location Review Tab */}
          <TabsContent value="location">
            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-teal-500" />
                  Location Tracking Review
                </CardTitle>
              </CardHeader>
              <CardContent>
                {shiftsWithLocation.length === 0 ? (
                  <div className="text-center py-8">
                    <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No shifts with location data</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Legend */}
                    <div className="flex items-center gap-4 text-sm bg-slate-50 p-3 rounded-lg">
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        Verified
                      </span>
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        Unverified
                      </span>
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        Expected Location
                      </span>
                    </div>
                    
                    {shiftsWithLocation.map((shift) => (
                      <div key={shift.shift_id} className="p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              shift.location_verified === true ? 'bg-teal-100' :
                              shift.location_verified === false ? 'bg-rose-100' : 'bg-slate-100'
                            }`}>
                              {shift.location_verified === true ? (
                                <CheckCircle className="w-6 h-6 text-teal-600" />
                              ) : shift.location_verified === false ? (
                                <AlertTriangle className="w-6 h-6 text-rose-600" />
                              ) : (
                                <MapPin className="w-6 h-6 text-slate-400" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={
                                  shift.location_verified === true ? 'bg-teal-100 text-teal-700' :
                                  shift.location_verified === false ? 'bg-rose-100 text-rose-700' :
                                  'bg-slate-100 text-slate-700'
                                }>
                                  {shift.location_verified === true ? 'Verified' : 
                                   shift.location_verified === false ? 'Unverified' : 'No GPS'}
                                </Badge>
                                {shift.location_distance !== null && (
                                  <Badge variant="outline" className="font-mono">
                                    {shift.location_distance >= 1000 
                                      ? `${(shift.location_distance / 1000).toFixed(1)} km away`
                                      : `${shift.location_distance.toFixed(0)} m away`
                                    }
                                  </Badge>
                                )}
                                <Badge variant="outline">{shift.status}</Badge>
                              </div>
                              <p className="font-medium text-slate-900">{shift.caregiver_name}</p>
                              <p className="text-sm text-slate-500">{shift.caregiver_email}</p>
                              <p className="text-xs text-slate-400 mt-1">
                                {new Date(shift.clock_in).toLocaleString()}
                                {shift.total_hours && ` • ${shift.total_hours.toFixed(2)} hours`}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {shift.clock_in_location && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => { setSelectedShiftForMap(shift); setMapDialogOpen(true); }}
                                data-testid={`view-map-btn-${shift.shift_id}`}
                              >
                                <Navigation className="w-4 h-4" />
                                View Map
                              </Button>
                            )}
                            {shift.location_verified === false && shift.status === 'pending_approval' && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-teal-500 hover:bg-teal-600 text-white"
                                  onClick={() => handleResolveShift(shift.shift_id, 'approve')}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-rose-600 border-rose-200"
                                  onClick={() => handleResolveShift(shift.shift_id, 'flag')}
                                >
                                  <Flag className="w-4 h-4" />
                                  Flag
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Flagged Shifts */}
          <TabsContent value="flagged">
            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope']">Flagged Shifts</CardTitle>
              </CardHeader>
              <CardContent>
                {flaggedShifts.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-teal-300 mx-auto mb-3" />
                    <p className="text-slate-500">No flagged shifts</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {flaggedShifts.map((shift) => (
                      <div key={shift.shift_id} className="p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <Badge className="bg-rose-100 text-rose-700 mb-2">Flagged</Badge>
                            <p className="font-medium text-slate-900">
                              {shift.caregiver_name} ({shift.caregiver_email})
                            </p>
                            <p className="text-sm text-slate-600">Hours: {shift.total_hours?.toFixed(2)}</p>
                            {shift.flagged_reason && (
                              <p className="text-sm text-rose-600 mt-1">
                                <AlertTriangle className="w-4 h-4 inline mr-1" />
                                {shift.flagged_reason}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-teal-500 hover:bg-teal-600 text-white"
                              onClick={() => handleResolveShift(shift.shift_id, 'approve')}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-rose-600"
                              onClick={() => handleResolveShift(shift.shift_id, 'reject')}
                            >
                              Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope']">All Users</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {users.map((u) => (
                    <div key={u.user_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center overflow-hidden">
                          {u.picture ? (
                            <img src={u.picture} alt={u.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-lg font-bold text-teal-700">{u.name?.[0] || '?'}</span>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{u.name}</p>
                          <p className="text-sm text-slate-500">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="secondary" className="capitalize">{u.role?.replace('_', ' ')}</Badge>
                        {u.trust_level === 'identity_verified' && (
                          <Badge className="bg-teal-100 text-teal-700">
                            <Shield className="w-3 h-3 mr-1" />Verified
                          </Badge>
                        )}
                        {!u.suspended && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-rose-600 hover:bg-rose-50"
                            onClick={() => handleSuspendUser(u.user_id)}
                          >
                            Suspend
                          </Button>
                        )}
                        {u.suspended && <Badge className="bg-rose-100 text-rose-700">Suspended</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* ID Verification Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review ID Verification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedVerification && (
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-medium">{selectedVerification.user_name}</p>
                <p className="text-sm text-slate-500">{selectedVerification.user_email}</p>
              </div>
            )}
            {documentPreview && (
              <img src={documentPreview} alt="ID Document" className="w-full max-h-96 object-contain bg-slate-100 rounded-lg" />
            )}
            <Textarea
              placeholder="Rejection reason (if rejecting)..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
            <div className="flex gap-3">
              <Button onClick={() => handleReviewVerification('approve')} className="flex-1 bg-teal-500 hover:bg-teal-600 text-white">
                <CheckCircle className="w-4 h-4" />Approve
              </Button>
              <Button onClick={() => handleReviewVerification('reject')} variant="outline" className="flex-1 text-rose-600">
                <XCircle className="w-4 h-4" />Reject
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Background Check Complete Dialog */}
      <Dialog open={bgCheckDialogOpen} onOpenChange={setBgCheckDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Complete Background Check</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedBgCheck && (
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-medium">{selectedBgCheck.user_name}</p>
                <p className="text-sm text-slate-500">{selectedBgCheck.user_email}</p>
                <p className="text-sm text-slate-500">Package: {selectedBgCheck.package_name}</p>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">Result</label>
              <Select value={bgCheckResult} onValueChange={setBgCheckResult}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="clear">Clear - No Issues Found</SelectItem>
                  <SelectItem value="flagged">Flagged - Issues Found</SelectItem>
                  <SelectItem value="failed">Failed - Unable to Complete</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                placeholder="Add any notes about the results..."
                value={bgCheckNotes}
                onChange={(e) => setBgCheckNotes(e.target.value)}
              />
            </div>
            <Button onClick={handleCompleteBgCheck} className="w-full bg-teal-500 hover:bg-teal-600 text-white">
              <CheckCircle className="w-4 h-4" />
              Complete Background Check
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Location Map Dialog */}
      <Dialog open={mapDialogOpen} onOpenChange={setMapDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-teal-500" />
              Shift Location Review
            </DialogTitle>
          </DialogHeader>
          {selectedShiftForMap && (
            <div className="space-y-4">
              {/* Shift Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Caregiver</p>
                    <p className="font-medium">{selectedShiftForMap.caregiver_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Clock In Time</p>
                    <p className="font-medium">{new Date(selectedShiftForMap.clock_in).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Location Status</p>
                    <Badge className={selectedShiftForMap.location_verified ? 'bg-teal-100 text-teal-700' : 'bg-rose-100 text-rose-700'}>
                      {selectedShiftForMap.location_verified ? 'Verified' : 'Not Verified'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Distance</p>
                    <p className="font-medium font-mono">
                      {selectedShiftForMap.location_distance !== null 
                        ? selectedShiftForMap.location_distance >= 1000 
                          ? `${(selectedShiftForMap.location_distance / 1000).toFixed(2)} km`
                          : `${selectedShiftForMap.location_distance.toFixed(0)} meters`
                        : 'N/A'
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Map */}
              {selectedShiftForMap.clock_in_location && (
                <div className="h-[400px] rounded-lg overflow-hidden border border-slate-200">
                  <MapContainer
                    center={[
                      selectedShiftForMap.clock_in_location.latitude,
                      selectedShiftForMap.clock_in_location.longitude
                    ]}
                    zoom={14}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    
                    {/* Caregiver Clock-In Location */}
                    <Marker 
                      position={[
                        selectedShiftForMap.clock_in_location.latitude,
                        selectedShiftForMap.clock_in_location.longitude
                      ]}
                      icon={selectedShiftForMap.location_verified ? verifiedIcon : unverifiedIcon}
                    >
                      <Popup>
                        <div className="text-sm">
                          <p className="font-bold">Clock-In Location</p>
                          <p>{selectedShiftForMap.caregiver_name}</p>
                          <p className="text-xs text-slate-500">
                            {selectedShiftForMap.clock_in_location.latitude.toFixed(6)}, 
                            {selectedShiftForMap.clock_in_location.longitude.toFixed(6)}
                          </p>
                          <p className="text-xs">Accuracy: ±{selectedShiftForMap.clock_in_location.accuracy?.toFixed(0) || 'N/A'}m</p>
                        </div>
                      </Popup>
                    </Marker>

                    {/* Expected Location (if available) */}
                    {selectedShiftForMap.expected_location && (
                      <>
                        <Marker
                          position={[
                            selectedShiftForMap.expected_location.latitude,
                            selectedShiftForMap.expected_location.longitude
                          ]}
                          icon={expectedIcon}
                        >
                          <Popup>
                            <div className="text-sm">
                              <p className="font-bold">Expected Location</p>
                              <p>{selectedShiftForMap.expected_location.address || 'Care Recipient Address'}</p>
                            </div>
                          </Popup>
                        </Marker>
                        
                        {/* 500m radius circle around expected location */}
                        <Circle
                          center={[
                            selectedShiftForMap.expected_location.latitude,
                            selectedShiftForMap.expected_location.longitude
                          ]}
                          radius={500}
                          pathOptions={{ 
                            color: '#14B8A6', 
                            fillColor: '#14B8A6', 
                            fillOpacity: 0.1,
                            weight: 2,
                            dashArray: '5, 5'
                          }}
                        />
                      </>
                    )}
                  </MapContainer>
                </div>
              )}

              {/* Coordinates Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-slate-500 mb-1">Clock-In Coordinates</p>
                  <p className="font-mono">
                    {selectedShiftForMap.clock_in_location?.latitude.toFixed(6)}, 
                    {selectedShiftForMap.clock_in_location?.longitude.toFixed(6)}
                  </p>
                </div>
                {selectedShiftForMap.expected_location && (
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-slate-500 mb-1">Expected Coordinates</p>
                    <p className="font-mono">
                      {selectedShiftForMap.expected_location.latitude.toFixed(6)}, 
                      {selectedShiftForMap.expected_location.longitude.toFixed(6)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
